import { useEffect, useRef, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import clsx from "clsx";
import { IMessages } from "../types/message-types";
import { ParseErrors } from "../utils/parse-errors";
import UseChaseProvider from "../utils/use-chase-provider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowUp, faRotateRight, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import iconImg from "../assets/myportal-icon.png";

const ChatCard = () => {
    const { chase, ToggleMessage } = UseChaseProvider()
    const messageList = useRef<HTMLDivElement | null>(null)
    const [messages, setMessages] = useState<IMessages[]>([])
    const [value, setValue] = useState<string>("")
    const [totalCount, setTotalCount] = useState<number>(0)
    const [limit,] = useState<number>(10)


    // -------------------------- [ HANDLE SEND MESSAGE ] -----------------------------------
    const HandleSendMessage = async () => {
        try {
            const res = await axiosInstance.post(`/message/public/send/${chase?.policyReferenceNumber}`, {
                message: value,
            })
            setMessages((prev) => ([...prev, res.data.data]))
            setValue("")
        } catch (error) {
            toast.error(ParseErrors(error))
        }
    }


    // -------------------------- [ FETCH CHASE MESSAGES ] -----------------------------------
    useEffect(() => {
        const findMessages = async () => {
            try {
                const res = await axiosInstance.get(`/message/public/${chase?.policyReferenceNumber}`)
                setMessages(res.data.data.sort((a: any, b: any) => a.id - b.id))
                setTotalCount(res.data.totalCount)
            } catch (error) {
                console.log(error)
            }
        }

        findMessages()
    }, [chase?.policyReferenceNumber])


    // --------------------------------- [ LOAD RECENT CHATS ] ------------------------

    const loadRecentChats = async () => {
        try {
            const res = await axiosInstance.get(`/message/public/${chase?.policyReferenceNumber}?offset=${messages.length}&limit=${limit}`)
            setMessages((prev) => [...res.data.data.sort((a: any, b: any) => a.id - b.id), ...prev])
        } catch (error) {
            console.log(error)
        }
    }


    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && value) {
            HandleSendMessage()
        }
    }

    useEffect(() => {
        if (messageList.current) {
            messageList.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [messages])


    return (
        <div
            className="w-full max-w-[520px] surface-card overflow-hidden h-[78vh] max-h-[760px] min-h-[480px] flex flex-col animate-drop-in"
            id="messageBox"
        >
            <div className="shrink-0 h-[68px] grid grid-cols-[40px_1fr_40px] items-center gap-2 px-3 sm:px-5 border-b border-white/[0.12]">
                <button type="button" aria-label="Back" onClick={ToggleMessage} className="w-10 h-10 rounded-full flex items-center justify-center text-white bg-app-row border-[1.5px] border-[rgba(91,120,255,0.55)] hover:bg-[#16204A] active:scale-[0.96] transition-[background-color,transform] duration-[120ms]">
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <h2 className="text-title text-white text-center">Messages</h2>
                <span />
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
                <div className="flex-1 min-h-0 px-4 py-5 sm:px-5 overflow-y-auto thin-scroll">
                    {(totalCount - messages.length) > 0 && <div className="w-full flex items-center justify-center pb-4">
                        <button type="button" onClick={loadRecentChats} className="h-8 px-3.5 rounded-full text-[13px] font-semibold text-white/[0.75] bg-white/[0.06] border border-white/[0.15] hover:text-white hover:bg-white/[0.12] active:scale-[0.96] transition-[color,background-color,transform] duration-[120ms] flex items-center gap-2">
                            <FontAwesomeIcon icon={faRotateRight} className="text-[11px]" /> Load Recent Chats
                        </button>
                    </div>}
                    <div className="flex flex-col gap-2">
                        {messages.map((item, index) => {

                            if (item.type === "portal") {
                                return (
                                    <div className="w-full flex items-center justify-start animate-fade-in" key={item?.id}>
                                        <div className="max-w-[82%] min-w-[64px] w-fit px-4 py-2.5 yourmessage">
                                            <h1 className="text-[14px] leading-relaxed font-medium break-words whitespace-pre-wrap">
                                                {item.message}
                                            </h1>
                                        </div>
                                    </div>
                                )
                            }

                            // only the first reply in a run from the agent shows the icon and name
                            const previous = messages[index - 1]
                            const startsRun = !previous || previous.type === "portal"

                            return (
                                <div className={clsx("w-full flex items-start gap-2.5 animate-fade-in", startsRun && previous && "mt-2")} key={item?.id} data-testid="agent-message">
                                    <span className="w-8 h-8 shrink-0">
                                        {startsRun && <img src={iconImg} alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-white/10" />}
                                    </span>
                                    <div className="max-w-[78%] min-w-0 flex flex-col items-start">
                                        {startsRun && <p className="ml-1 mb-1 text-[12px] font-bold text-white/[0.66]">DocuSure</p>}
                                        <div className="min-w-[64px] w-fit px-4 py-2.5 usermessage !mr-0">
                                            <h1 className="text-[14px] leading-relaxed font-medium break-words whitespace-pre-wrap">
                                                {item.message}
                                            </h1>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                        <div ref={messageList}></div>
                    </div>

                </div>
                {value?.length ? <div className="shrink-0 w-full px-4 pt-3 animate-fade-in"><div className="flex gap-3 items-start p-3 rounded-toast bg-[rgba(255,224,70,0.08)] border border-[rgba(255,224,70,0.28)]">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="text-warn mt-[3px]" />
                    <p className="text-[13px] leading-[19px] text-white/[0.92] font-semibold">For security reasons, please do not share any sensitive details, such as your license number in this chat. Please submit via the form.</p>
                </div> </div> : null}
                <div className="shrink-0 px-4 pt-3 pb-4 w-full" id="chat-input-bx">
                    <div className="h-[54px] pl-4 pr-1.5 rounded-2xl bg-app-field border border-white/[0.12] flex items-center gap-2 transition-[box-shadow,border-color] duration-200 focus-within:border-brand-accent focus-within:shadow-focus">
                        <input
                            type="text"
                            value={value}
                            aria-label="Message"
                            className="font-semibold text-[16px] text-white w-full bg-transparent outline-none focus-visible:outline-none placeholder:text-white/[0.55] placeholder:font-medium"
                            placeholder="Enter Message"
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <button onClick={HandleSendMessage} disabled={!value} aria-label="Send message" className="shrink-0 w-[42px] h-[42px] rounded-xl bg-cta text-white flex items-center justify-center shadow-cta transition-[transform,filter,opacity] duration-[120ms] hover:brightness-110 active:scale-[0.96] disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed">
                            <FontAwesomeIcon icon={faArrowUp} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ChatCard
